import { create } from "zustand";
import { Entity } from "@/services/entity.service";
import { EntityField, EntityRelated } from "@/services/entity.service";

interface EntityStoreState {
  entityMapForNames: Map<string, Entity>;
  entityMapForIds: Map<string, Entity>;
  setEntityList: (entities: Entity[]) => void;
  clearEntities: () => void;
  getEntityByName: (entityName: string) => Entity | undefined;
  getEntityFields: (entityName: string) => Entity["entity_fields"] | undefined;
  getAllRelations: (entityName: string) => EntityRelated[] | undefined;
  getAllEntityNames: () => string[];
  getFieldOrRelationByPath: (path: string) => EntityField | EntityRelated | undefined;
}

export const useEntityStore = create<EntityStoreState>((set, get) => ({
  entityMapForNames: new Map(),
  entityMapForIds: new Map(),

  setEntityList: (entities: Entity[]) => {
    const entityMapByName = new Map<string, Entity>();
    const entityMapById = new Map<string, Entity>();

    entities.forEach((entity) => {
      const relations = entity.entity_relations?.data || [];
      const related = entity.entity_related?.data || [];

      // Normalize entity_related entries by flipping entity/target_entity and field_name/target_field_name.
      // Without a target_field_name the backend generates no inverse GraphQL field
      // (GraphQLService::generateOutput/InputRelations skip it), so the relation does not
      // exist in this direction. Never borrow the source entity's field_name for it — that
      // invents a relation whose name can shadow a real scalar field of the same name.
      const normalizedRelated = related
        .filter(
          (rel): rel is EntityRelated & { target_field_name: string } => !!rel.target_field_name,
        )
        .map((rel) => {
          const flippedType = (() => {
            switch (rel.type) {
              case "OneToMany":
                return "ManyToOne";
              case "ManyToOne":
                return "OneToMany";
              case "OneToOne":
              case "ManyToMany":
              default:
                return rel.type;
            }
          })();

          return {
            ...rel,
            type: flippedType,
            // Flip entity and target_entity
            entity: rel.target_entity,
            target_entity: rel.entity,
            // Flip field_name and target_field_name
            field_name: rel.target_field_name,
            target_field_name: rel.field_name,
            // Flip tightly coupled flags
            entity_is_tightly_coupled: rel.target_entity_is_tightly_coupled,
            target_entity_is_tightly_coupled: rel.entity_is_tightly_coupled,
          };
        });

      // Attach combined_relations to each entity (non-breaking augmentation)
      const augmentedEntity = {
        ...entity,
        combined_relations: [...relations, ...normalizedRelated],
      } as Entity & { combined_relations: EntityRelated[] };

      entityMapByName.set(entity.name, augmentedEntity);
      entityMapById.set(entity.id, augmentedEntity);
    });

    set({ entityMapForNames: entityMapByName, entityMapForIds: entityMapById });
  },

  getEntityByName: (entityName: string) => {
    return get().entityMapForNames.get(entityName);
  },

  getEntityFields: (entityName: string) => {
    const entity = get().entityMapForNames.get(entityName);
    return entity?.entity_fields;
  },

  getAllRelations: (entityName: string) => {
    const entity = get().entityMapForNames.get(entityName);
    if (!entity) return undefined;
    // Return precomputed combined_relations only
    const anyEntity = entity as Entity & {
      combined_relations?: EntityRelated[];
    };
    return anyEntity.combined_relations ?? [];
  },

  getAllEntityNames: () => {
    return Array.from(get().entityMapForNames.keys());
  },

  getFieldOrRelationByPath: (path: string) => {
    const pathParts = path.split(".");
    if (pathParts.length === 0) return undefined;

    const state = get();
    let currentEntityName = pathParts[0];
    let currentEntity = state.entityMapForNames.get(currentEntityName);

    if (!currentEntity) return undefined;

    // If it's just a single entity name, return undefined (not a field/relation)
    if (pathParts.length === 1) {
      return undefined;
    }

    // Navigate through the path
    for (let i = 1; i < pathParts.length; i++) {
      const fieldName = pathParts[i];

      // Check in entity fields
      const field = currentEntity.entity_fields?.data.find((f) => f.name === fieldName);
      if (field) {
        // If this is the last part, return the field
        if (i === pathParts.length - 1) {
          return field;
        }
        // Otherwise, continue with the field's target entity if it's a relation
        continue;
      }

      // Check in all relations (merged and normalized)
      const allRelations = get().getAllRelations(currentEntityName);
      const relation = allRelations?.find((r) => r.field_name === fieldName);
      if (relation) {
        // Get the target entity
        const targetEntityName = relation.target_entity.data.name;
        currentEntity = state.entityMapForNames.get(targetEntityName);

        if (!currentEntity) return undefined;

        // If this is the last part, return the relation
        if (i === pathParts.length - 1) {
          return relation;
        }
        continue;
      }

      // Field/relation not found
      return undefined;
    }

    return undefined;
  },

  clearEntities: () => {
    set({ entityMapForNames: new Map() });
  },
}));
